import { Button, DetailList, formatDate, PageEmpty, PageError, PageHeader, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { useRecordCrumb } from "../../crumb";
import { messageFor } from "../../error-copy";
import { meQuery } from "../../session";
import { type StaffMember, staffActionsFor, staffBadge, staffQuery, teamActions } from "../../team";
import { ConfirmDialog } from "../shared/confirm";
import { ActivityPanel } from "../shared/panels";
import styles from "./settings.module.css";
import { RolePicker } from "./staff";

/** One staff member (P8-22): their roles, their access, a fresh invitation or password, their history. */
export function StaffMemberPage(): ReactNode {
  const { staffId } = useParams({ from: "/console/settings/staff/$staffId" });
  const member = useQuery(staffQuery(staffId));
  useRecordCrumb(member.data?.user.name);
  if (member.status === "pending") return <PageLoading label="Loading" />;
  if (member.status === "error") {
    const error = errorOf(member.error);
    if (error.code === "STAFF_NOT_FOUND")
      return <PageEmpty title="No such staff member" description="Check the link." />;
    return <PageError message={messageFor(error)} onRetry={() => member.refetch()} />;
  }
  return <MemberView member={member.data} />;
}

function MemberView({ member: m }: { member: StaffMember }) {
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  const can = staffActionsFor(m);
  const [roles, setRoles] = useState(m.roles.map((r) => r.id));
  const [dialog, setDialog] = useState<"deactivate" | "reactivate" | "resend" | "reset" | "edit" | null>(null);
  const [notice, setNotice] = useState<string>();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["staff"] });
  const saveRoles = useMutation({
    mutationFn: () => teamActions.setRoles(m.id, roles),
    onSuccess: async () => {
      await refresh();
      setNotice("Roles saved. They apply when this person next loads a page.");
    },
  });
  const [label, tone] = staffBadge(m);
  const changed =
    [...roles].sort().join() !==
    m.roles
      .map((r) => r.id)
      .sort()
      .join();
  return (
    <>
      <PageHeader
        title={m.user.name}
        description={[m.position, m.user.email].filter(Boolean).join(" · ")}
        actions={
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setDialog("edit")}>
              Edit details
            </Button>
            {can.resend ? (
              <Button variant="secondary" onClick={() => setDialog("resend")}>
                {m.invitation_pending ? "Resend the invitation" : "Send a sign-in reminder"}
              </Button>
            ) : null}
            {can.resetPassword ? (
              <Button variant="secondary" onClick={() => setDialog("reset")}>
                Send a password reset link
              </Button>
            ) : null}
            {can.deactivate ? (
              <Button variant="secondary" onClick={() => setDialog("deactivate")}>
                Deactivate
              </Button>
            ) : null}
            {can.reactivate ? <Button onClick={() => setDialog("reactivate")}>Reactivate</Button> : null}
          </div>
        }
      />
      {m.invitation_pending ? (
        <Alert tone="warning" title="They haven't set their password yet">
          The invitation link works for 72 hours. If it lapsed, or they can't find the email, resend it.
        </Alert>
      ) : null}
      {notice ? (
        <Alert tone="success" title="Done">
          {notice}
        </Alert>
      ) : null}
      <div className={styles.columns}>
        <div className={styles.main}>
          <section className={styles.card}>
            <DetailList
              items={[
                { label: "Status", value: <Badge tone={tone}>{label}</Badge> },
                { label: "Email", value: m.user.email },
                { label: "Phone", value: m.user.phone_number },
                { label: "Position", value: m.position },
                { label: "Added", value: formatDate(m.created_at, format) },
              ]}
            />
          </section>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Roles</h2>
            <RolePicker value={roles} onChange={setRoles} />
            {saveRoles.error ? (
              <Alert tone="danger" title="Roles not saved">
                {messageFor(errorOf(saveRoles.error))}
              </Alert>
            ) : null}
            <div>
              <Button
                disabled={!changed || roles.length === 0}
                loading={saveRoles.isPending}
                onClick={() => saveRoles.mutate()}
              >
                Save the roles
              </Button>
            </div>
          </section>
          {me?.permissions.includes("staff.manage") ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Activity</h2>
              <ActivityPanel targetType="staff" id={m.id} label="What changed for this person" />
            </section>
          ) : null}
        </div>
      </div>
      {dialog === "edit" ? (
        <EditMember
          member={m}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            await refresh();
            setNotice("Details saved.");
          }}
        />
      ) : null}
      {dialog === "resend" ? (
        <ConfirmDialog
          title={m.invitation_pending ? "Resend the invitation?" : "Send a sign-in reminder?"}
          description={
            m.invitation_pending
              ? `${m.user.email} gets a new link to set their password, valid 72 hours. Older links stop working.`
              : `They already have a password: ${m.user.email} gets a note to sign in.`
          }
          action="Send"
          run={() => teamActions.resend(m.id)}
          onDone={() => setNotice(`Sent to ${m.user.email}.`)}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "reset" ? (
        <ConfirmDialog
          title="Send a password reset link?"
          description={`${m.user.email} gets a link to choose a new password, valid an hour. Their password works until they set the new one; then they're signed out everywhere.`}
          action="Send"
          run={() => teamActions.resetPassword(m.id)}
          onDone={() => setNotice(`Reset link sent to ${m.user.email}.`)}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "deactivate" ? (
        <ConfirmDialog
          title={`Deactivate ${m.user.name}?`}
          description="They're signed out of the console at once and can't sign in again. Their history stays. Reactivate to undo it."
          action="Deactivate"
          danger
          run={() => teamActions.deactivate(m.id)}
          onDone={refresh}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "reactivate" ? (
        <ConfirmDialog
          title={`Reactivate ${m.user.name}?`}
          description="They can sign in again with their password, with their roles as they were."
          action="Reactivate"
          run={() => teamActions.reactivate(m.id)}
          onDone={refresh}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}

/** Correct a name or position. The email and phone are how they sign in, so they stay. */
function EditMember({
  member: m,
  onClose,
  onSaved,
}: {
  member: StaffMember;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const [name, setName] = useState(m.user.name);
  const [position, setPosition] = useState(m.position ?? "");
  const save = useMutation({
    mutationFn: () => teamActions.update(m.id, { name: name.trim(), position: position.trim() || null }),
    onSuccess: async () => {
      await onSaved();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Edit ${m.user.name}`}
        description="Their email and phone are how they sign in, so they don't change here."
      >
        <div className={styles.form}>
          <div className={styles.pair}>
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <Input label="Position (optional)" value={position} onChange={(e) => setPosition(e.target.value)} />
          </div>
          {save.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(save.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!name.trim()} loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
